import i18n from "@/utils/i18n";

/**
 * Yetki matrisi - accordion kategorileri ve backend permission key'leri
 * Her kategori: label (i18n) + permissions (action -> key + description)
 *
 * "Sayfa Erişimi" (MODULE.PAGE) her kategorinin ilk satırıdır ve modüle
 * erişimin master anahtarıdır — verilmezse backend hiçbir endpoint'i
 * çalıştırmaz, sol menüde sayfa görünmez.
 */
export interface PermissionItem {
  label: string;
  key: string;
  description: string;
}

export interface PermissionCategory {
  id: string;
  label: string;
  permissions: PermissionItem[];
}

const t = (k: string) => i18n.t(k);
const pageRow = (key: string): PermissionItem => ({
  label: t("permissionMatrix.actions.page"),
  key,
  description: t("permissionMatrix.pageDescription"),
});

export const PERMISSION_MATRIX: PermissionCategory[] = [
  {
    id: "announcements",
    label: t("permissionMatrix.categories.announcements"),
    permissions: [
      pageRow("ANNOUNCEMENTS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "ANNOUNCEMENTS.VIEW", description: t("permissionMatrix.descriptions.announcementsView") },
      { label: t("permissionMatrix.actions.create"), key: "ANNOUNCEMENTS.CREATE", description: t("permissionMatrix.descriptions.announcementsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "ANNOUNCEMENTS.UPDATE", description: t("permissionMatrix.descriptions.announcementsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "ANNOUNCEMENTS.DELETE", description: t("permissionMatrix.descriptions.announcementsDelete") },
    ],
  },
  {
    id: "banned_users",
    label: t("permissionMatrix.categories.bannedUsers"),
    permissions: [
      pageRow("BANNED_USERS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "BANNED_USERS.VIEW", description: t("permissionMatrix.descriptions.bannedUsersView") },
      { label: t("permissionMatrix.actions.create"), key: "BANNED_USERS.CREATE", description: t("permissionMatrix.descriptions.bannedUsersCreate") },
      { label: t("permissionMatrix.actions.update"), key: "BANNED_USERS.UPDATE", description: t("permissionMatrix.descriptions.bannedUsersUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "BANNED_USERS.DELETE", description: t("permissionMatrix.descriptions.bannedUsersDelete") },
    ],
  },
  {
    id: "education_categories",
    label: t("permissionMatrix.categories.educationCategories"),
    permissions: [
      pageRow("EDUCATION_CATEGORIES.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "EDUCATION_CATEGORIES.VIEW", description: t("permissionMatrix.descriptions.educationCategoriesView") },
      { label: t("permissionMatrix.actions.create"), key: "EDUCATION_CATEGORIES.CREATE", description: t("permissionMatrix.descriptions.educationCategoriesCreate") },
      { label: t("permissionMatrix.actions.update"), key: "EDUCATION_CATEGORIES.UPDATE", description: t("permissionMatrix.descriptions.educationCategoriesUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "EDUCATION_CATEGORIES.DELETE", description: t("permissionMatrix.descriptions.educationCategoriesDelete") },
    ],
  },
  {
    id: "departments",
    label: t("permissionMatrix.categories.departments"),
    permissions: [
      pageRow("DEPARTMENTS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "DEPARTMENTS.VIEW", description: t("permissionMatrix.descriptions.departmentsView") },
      { label: t("permissionMatrix.actions.create"), key: "DEPARTMENTS.CREATE", description: t("permissionMatrix.descriptions.departmentsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "DEPARTMENTS.UPDATE", description: t("permissionMatrix.descriptions.departmentsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "DEPARTMENTS.DELETE", description: t("permissionMatrix.descriptions.departmentsDelete") },
    ],
  },
  {
    id: "educations",
    label: t("permissionMatrix.categories.educations"),
    permissions: [
      pageRow("EDUCATIONS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "EDUCATIONS.VIEW", description: t("permissionMatrix.descriptions.educationsView") },
      { label: t("permissionMatrix.actions.create"), key: "EDUCATIONS.CREATE", description: t("permissionMatrix.descriptions.educationsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "EDUCATIONS.UPDATE", description: t("permissionMatrix.descriptions.educationsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "EDUCATIONS.DELETE", description: t("permissionMatrix.descriptions.educationsDelete") },
      { label: t("permissionMatrix.actions.approve"), key: "EDUCATIONS.APPROVE", description: t("permissionMatrix.descriptions.educationsApprove") },
      { label: t("permissionMatrix.actions.enroll"), key: "EDUCATIONS.ENROLL", description: t("permissionMatrix.descriptions.educationsEnroll") },
    ],
  },
  {
    id: "certificates",
    label: t("permissionMatrix.categories.certificates"),
    permissions: [
      pageRow("CERTIFICATES.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "CERTIFICATES.VIEW", description: t("permissionMatrix.descriptions.certificatesView") },
      { label: t("permissionMatrix.actions.create"), key: "CERTIFICATES.CREATE", description: t("permissionMatrix.descriptions.certificatesCreate") },
      { label: t("permissionMatrix.actions.update"), key: "CERTIFICATES.UPDATE", description: t("permissionMatrix.descriptions.certificatesUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "CERTIFICATES.DELETE", description: t("permissionMatrix.descriptions.certificatesDelete") },
      { label: t("permissionMatrix.actions.assignCertificate"), key: "CERTIFICATES.ASSIGN", description: t("permissionMatrix.descriptions.certificatesAssign") },
      { label: t("permissionMatrix.actions.templatesView"), key: "CERTIFICATES.TEMPLATES_VIEW", description: t("permissionMatrix.descriptions.certificatesTemplatesView") },
      { label: t("permissionMatrix.actions.templatesCreate"), key: "CERTIFICATES.TEMPLATES_CREATE", description: t("permissionMatrix.descriptions.certificatesTemplatesCreate") },
      { label: t("permissionMatrix.actions.templatesUpdate"), key: "CERTIFICATES.TEMPLATES_UPDATE", description: t("permissionMatrix.descriptions.certificatesTemplatesUpdate") },
      { label: t("permissionMatrix.actions.templatesDelete"), key: "CERTIFICATES.TEMPLATES_DELETE", description: t("permissionMatrix.descriptions.certificatesTemplatesDelete") },
    ],
  },
  {
    id: "exams",
    label: t("permissionMatrix.categories.exams"),
    permissions: [
      pageRow("EXAMS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "EXAMS.VIEW", description: t("permissionMatrix.descriptions.examsView") },
      { label: t("permissionMatrix.actions.create"), key: "EXAMS.CREATE", description: t("permissionMatrix.descriptions.examsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "EXAMS.UPDATE", description: t("permissionMatrix.descriptions.examsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "EXAMS.DELETE", description: t("permissionMatrix.descriptions.examsDelete") },
    ],
  },
  {
    id: "surveys",
    label: t("permissionMatrix.categories.surveys"),
    permissions: [
      pageRow("SURVEYS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "SURVEYS.VIEW", description: t("permissionMatrix.descriptions.surveysView") },
      { label: t("permissionMatrix.actions.create"), key: "SURVEYS.CREATE", description: t("permissionMatrix.descriptions.surveysCreate") },
      { label: t("permissionMatrix.actions.update"), key: "SURVEYS.UPDATE", description: t("permissionMatrix.descriptions.surveysUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "SURVEYS.DELETE", description: t("permissionMatrix.descriptions.surveysDelete") },
    ],
  },
  {
    id: "files",
    label: t("permissionMatrix.categories.files"),
    permissions: [
      pageRow("FILES.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "FILES.VIEW", description: t("permissionMatrix.descriptions.filesView") },
      { label: t("permissionMatrix.actions.create"), key: "FILES.CREATE", description: t("permissionMatrix.descriptions.filesCreate") },
      { label: t("permissionMatrix.actions.update"), key: "FILES.UPDATE", description: t("permissionMatrix.descriptions.filesUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "FILES.DELETE", description: t("permissionMatrix.descriptions.filesDelete") },
    ],
  },
  {
    id: "library",
    label: t("permissionMatrix.categories.library"),
    permissions: [
      pageRow("LIBRARY.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "LIBRARY.VIEW", description: t("permissionMatrix.descriptions.libraryView") },
      { label: t("permissionMatrix.actions.create"), key: "LIBRARY.CREATE", description: t("permissionMatrix.descriptions.libraryCreate") },
      { label: t("permissionMatrix.actions.update"), key: "LIBRARY.UPDATE", description: t("permissionMatrix.descriptions.libraryUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "LIBRARY.DELETE", description: t("permissionMatrix.descriptions.libraryDelete") },
    ],
  },
  {
    id: "users",
    label: t("permissionMatrix.categories.users"),
    permissions: [
      pageRow("USERS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "USERS.VIEW", description: t("permissionMatrix.descriptions.usersView") },
      { label: t("permissionMatrix.actions.viewAll"), key: "USERS.VIEW_ALL", description: t("permissionMatrix.descriptions.usersViewAll") },
      { label: t("permissionMatrix.actions.create"), key: "USERS.CREATE", description: t("permissionMatrix.descriptions.usersCreate") },
      { label: t("permissionMatrix.actions.update"), key: "USERS.UPDATE", description: t("permissionMatrix.descriptions.usersUpdate") },
      { label: t("permissionMatrix.actions.passwordChange"), key: "USERS.UPDATE_PASSWORD", description: t("permissionMatrix.descriptions.usersUpdatePassword") },
      { label: t("permissionMatrix.actions.delete"), key: "USERS.DELETE", description: t("permissionMatrix.descriptions.usersDelete") },
    ],
  },
  {
    id: "roles",
    label: t("permissionMatrix.categories.roles"),
    permissions: [
      pageRow("ROLES.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "ROLES.VIEW", description: t("permissionMatrix.descriptions.rolesView") },
      { label: t("permissionMatrix.actions.create"), key: "ROLES.CREATE", description: t("permissionMatrix.descriptions.rolesCreate") },
      { label: t("permissionMatrix.actions.update"), key: "ROLES.UPDATE", description: t("permissionMatrix.descriptions.rolesUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "ROLES.DELETE", description: t("permissionMatrix.descriptions.rolesDelete") },
      { label: t("permissionMatrix.actions.assign"), key: "ROLES.ASSIGN", description: t("permissionMatrix.descriptions.rolesAssign") },
      { label: t("permissionMatrix.actions.revoke"), key: "ROLES.REVOKE", description: t("permissionMatrix.descriptions.rolesRevoke") },
    ],
  },
  {
    id: "locations",
    label: t("permissionMatrix.categories.locations"),
    permissions: [
      pageRow("LOCATIONS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "LOCATIONS.VIEW", description: t("permissionMatrix.descriptions.locationsView") },
      { label: t("permissionMatrix.actions.create"), key: "LOCATIONS.CREATE", description: t("permissionMatrix.descriptions.locationsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "LOCATIONS.UPDATE", description: t("permissionMatrix.descriptions.locationsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "LOCATIONS.DELETE", description: t("permissionMatrix.descriptions.locationsDelete") },
    ],
  },
  {
    id: "support_tickets",
    label: t("permissionMatrix.categories.supportTickets"),
    permissions: [
      pageRow("SUPPORT_TICKETS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "SUPPORT_TICKETS.VIEW", description: t("permissionMatrix.descriptions.supportTicketsView") },
      { label: t("permissionMatrix.actions.create"), key: "SUPPORT_TICKETS.CREATE", description: t("permissionMatrix.descriptions.supportTicketsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "SUPPORT_TICKETS.UPDATE", description: t("permissionMatrix.descriptions.supportTicketsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "SUPPORT_TICKETS.DELETE", description: t("permissionMatrix.descriptions.supportTicketsDelete") },
      { label: t("permissionMatrix.actions.notify"), key: "SUPPORT_TICKETS.NOTIFY", description: t("permissionMatrix.descriptions.supportTicketsNotify") },
    ],
  },
  {
    id: "email_broadcast",
    label: t("permissionMatrix.categories.emailBroadcast"),
    permissions: [
      pageRow("EMAIL_BROADCAST.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "EMAIL_BROADCAST.VIEW", description: t("permissionMatrix.descriptions.emailBroadcastView") },
      { label: t("permissionMatrix.actions.send"), key: "EMAIL_BROADCAST.SEND", description: t("permissionMatrix.descriptions.emailBroadcastSend") },
    ],
  },
  {
    id: "audit_logs",
    label: t("permissionMatrix.categories.auditLogs"),
    permissions: [
      pageRow("AUDIT_LOGS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "AUDIT_LOGS.VIEW", description: t("permissionMatrix.descriptions.auditLogsView") },
    ],
  },
  {
    id: "reports",
    label: t("permissionMatrix.categories.reports"),
    permissions: [
      pageRow("REPORTS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "REPORTS.VIEW", description: t("permissionMatrix.descriptions.reportsView") },
    ],
  },
  {
    id: "approval_flows",
    label: t("permissionMatrix.categories.approvalFlows"),
    permissions: [
      pageRow("APPROVAL_FLOWS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "APPROVAL_FLOWS.VIEW", description: t("permissionMatrix.descriptions.approvalFlowsView") },
      { label: t("permissionMatrix.actions.create"), key: "APPROVAL_FLOWS.CREATE", description: t("permissionMatrix.descriptions.approvalFlowsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "APPROVAL_FLOWS.UPDATE", description: t("permissionMatrix.descriptions.approvalFlowsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "APPROVAL_FLOWS.DELETE", description: t("permissionMatrix.descriptions.approvalFlowsDelete") },
    ],
  },
  {
    id: "approval_requests",
    label: t("permissionMatrix.categories.approvalRequests"),
    permissions: [
      pageRow("APPROVAL_REQUESTS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "APPROVAL_REQUESTS.VIEW", description: t("permissionMatrix.descriptions.approvalRequestsView") },
      { label: t("permissionMatrix.actions.create"), key: "APPROVAL_REQUESTS.CREATE", description: t("permissionMatrix.descriptions.approvalRequestsCreate") },
      { label: t("permissionMatrix.actions.approve"), key: "APPROVAL_REQUESTS.APPROVE", description: t("permissionMatrix.descriptions.approvalRequestsApprove") },
      { label: t("permissionMatrix.actions.delete"), key: "APPROVAL_REQUESTS.DELETE", description: t("permissionMatrix.descriptions.approvalRequestsDelete") },
    ],
  },
  {
    id: "interns",
    label: t("permissionMatrix.categories.interns"),
    permissions: [
      pageRow("INTERNS.PAGE"),
      { label: t("permissionMatrix.actions.view"), key: "INTERNS.VIEW", description: t("permissionMatrix.descriptions.internsView") },
      { label: t("permissionMatrix.actions.create"), key: "INTERNS.CREATE", description: t("permissionMatrix.descriptions.internsCreate") },
      { label: t("permissionMatrix.actions.update"), key: "INTERNS.UPDATE", description: t("permissionMatrix.descriptions.internsUpdate") },
      { label: t("permissionMatrix.actions.delete"), key: "INTERNS.DELETE", description: t("permissionMatrix.descriptions.internsDelete") },
    ],
  },
];
